import React from 'react';
import { markComponentStart } from 'src/commons/utils/logWithTakt';
import { Components } from 'src/commons/constants';
import { performanceMark } from 'src/utils/performance-helper';
import { store } from 'src/app/store';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { InternalChromeConfigContextProvider } from 'src/providers/InternalChromeConfigContextProvider';
import { ConnectedChromeApp } from 'src/commons/components/ChromeApp/ChromeApp';
import { ConnectedNav } from 'src/features/nav/Nav';
import { SidenavBundleProvider } from 'src/providers/PantherLocalizationProvider';
import { getChromeData } from 'src/commons/apis';
import {
  initAjaxNavState,
  initInternalNavState,
  NavState,
} from 'src/features/nav/store/slice';
import { NavMetricPublisher } from 'src/commons/utils/metrics';
import { logger } from 'src/commons/utils/logger';
import { ChromeConfigContextProvider } from 'src/providers/ChromeConfigContextProvider';
import { isFromBtswWc, isFromTrim } from 'src/commons/utils';
import { ChromeSharedData } from '@amzn/aac-chrome-internal-api';
import { ErrorIdProvider } from '@amzn/storm-ui';

export default class AXChromeNav extends HTMLElement {
  static observedAttributes = ['initChromeSharedConfig'];

  constructor() {
    super();
  }

  set initChromeSharedConfig(initChromeSharedConfig: ChromeSharedData) {
    if (initChromeSharedConfig) {
      this.initBasicInfoFromTrim(initChromeSharedConfig);
    }
  }

  initBasicInfoFromTrim = (initChromeSharedConfig: ChromeSharedData) => {
    if (!initChromeSharedConfig) {
      return;
    }

    const { initialNavState, ...restChromeSharedData } = initChromeSharedConfig;
    store.dispatch(
      initInternalNavState({
        ...restChromeSharedData,
      } as NavState),
    );
  };

  async _renderNav() {
    if (window.__AAC_CHROME_HIDE_WC_NAVS_PROMISE__) {
      const hide = await window.__AAC_CHROME_HIDE_WC_NAVS_PROMISE__;
      if (hide) return;
    }

    markComponentStart(Components.NAV);
    const endMarker = performanceMark('aac-chrome-ui-nav');

    const initSideNavData = async () => {
      const initBasicInfo = async () => {
        const { chromeSharedData } = await getChromeData();
        const { initialNavState } = chromeSharedData;
        const { topToolbarItems, bottomToolbarItems, ...rest } =
          initialNavState;
        store.dispatch(
          initAjaxNavState({
            ...rest,
          }),
        );
      };

      try {
        await Promise.all([
          isFromTrim() || isFromBtswWc()
            ? this.initBasicInfoFromTrim(this['initChromeSharedConfig'])
            : initBasicInfo(),
        ]);
        NavMetricPublisher.publishCounterMonitor('initFailure', 0);
      } catch (e: any) {
        NavMetricPublisher.publishCounterMonitor('initFailure', 1);
        logger.error('failed to call getNavState:', e);
      }
    };

    initSideNavData();

    ReactDOM.render(
      <ErrorIdProvider id="Chrome">
        <Provider store={store}>
          <InternalChromeConfigContextProvider>
            <ChromeConfigContextProvider>
              <ConnectedChromeApp name="Nav">
                <SidenavBundleProvider>
                  <ConnectedNav />
                </SidenavBundleProvider>
              </ConnectedChromeApp>
            </ChromeConfigContextProvider>
          </InternalChromeConfigContextProvider>
        </Provider>
      </ErrorIdProvider>,
      this,
    );

    endMarker();
  }

  connectedCallback(): void {
    this._renderNav();
  }
}

if (!customElements.get('ax-chrome-nav')) {
  customElements.define('ax-chrome-nav', AXChromeNav);
}
